import { observable, computed, makeObservable, action } from 'mobx';

import { createModelSchema, primitive, object } from 'serializr';

import head from 'lodash/head';

import { Contact } from '@vromo/models';
import { JobTimeframe } from '@vromo/models';
import { Task } from '@vromo/models';
import { Note } from '@vromo/models';
import { KeyValue } from '@vromo/models';
import { Upload } from '@vromo/models';
import { Dispatch } from '@vromo/models';
import { Rating } from '@vromo/models';
import { Policy } from '@vromo/models';
import { Chat } from '@vromo/models';

import { EntityList, entityList } from '@vromo/models';
import { IndexList, indexList } from '@vromo/models';
import { Campaign } from '@vromo/models';
import { RouteLeg } from '@vromo/models';

import { Vehicle } from '@vromo/models';

import { Site } from '@vromo/models';

import timeStore from '@vromo/dashboard/src/stores/TimeStore';

import { optional } from '@vromo/models';

import { TimeFrame } from '@vromo/models';
import { JobPayable } from '@vromo/models';
import { LineItem } from '@vromo/models';
import { JobCancellation } from './JobCancellation';
import { JobIssue } from './JobIssue';
import { CourierInfo } from '../CourierInfo';

export type JobStage = {
    [key: string]: {
        campaign: {
            id: number;
            link: {
                mode: string;
            };
        };
        url: string;
        analytics: {
            clicksTotal: number;
        };
    };
};

// https://www.desmos.com/calculator/majepxavvf
function clamp(x: number) {
    return 1 / (1 + Math.pow(Math.E, -4.2 * (x - 0.5)));
}

export type JobStatus = 'open' | 'completed' | 'cancelled' | 'dispatched' | 'underway';

export class Job {
    id: number | null = null;
    created: number | null = null;
    name = '';
    group: number | null = null;
    status?: JobStatus = 'open';
    finished?: number | null = null;
    contact?: Contact | null = null;
    optedInForCampaignSms = true;
    timeframe: JobTimeframe | null = null;
    tracking: string | null = null;
    /** it should be populated for new job creation */
    tasks?: IndexList<Task> = new IndexList(Task);
    notes: EntityList<Note> | null = null;
    attr: EntityList<KeyValue> | null = new EntityList(KeyValue);
    uploads: EntityList<Upload> | null = null;
    chat: EntityList<Chat> | null = null;
    progress? = 0;
    dispatches: EntityList<Dispatch> | null = null;
    policy?: Policy | null;
    ratings?: EntityList<Rating>;
    campaign?: Campaign | null;
    cancelled: JobCancellation | null = null;
    offered?: EntityList<Vehicle> | null;
    site?: Site | null = null;
    route?: IndexList<RouteLeg>;
    issues?: EntityList<JobIssue>;
    external?: string;
    payable?: JobPayable | null = null;
    lines?: EntityList<LineItem>;
    instructions?: string;
    courierInfo: EntityList<CourierInfo>;
    stages?: JobStage;

    get underway(): Dispatch | undefined {
        return this.dispatches ? this.dispatches.find((it) => it.type === 'underway') : undefined;
    }

    get dispatched(): boolean {
        return !!(
            this.dispatches &&
            this.dispatches.last() &&
            this.dispatches.last()!.type &&
            ['dispatched', 'underway'].includes(this.dispatches!.last()!.type!)
        );
    }

    get nextDeadline(): number | undefined {
        const timeframes = this.tasks!.slice(this.progress)
            .filter((it) => it.timeframe !== null && it.timeframe.by !== null)
            .map((it) => it.timeframe as TimeFrame);

        const deadlines = timeframes.map((it) => it.by as number);

        return head(deadlines);
    }

    get risk(): number {
        let urgency = 1;
        const issue = this.issue && this.issue.type !== 'no_issue' ? 0 : 1;

        if (this.nextDeadline) {
            urgency *=
                (this.nextDeadline - timeStore.slowTime) /
                1000 /
                60 /
                (() => {
                    if (this.status === 'dispatched') return 20;
                    else if (this.status === 'underway') return 5;
                    else if (!this.vehicle && !(this.offered && this.offered.length)) return 90;
                    else if (!this.vehicle) return 40;
                    else return 90;
                })();
            return clamp(urgency) * issue;
        } else {
            return issue;
        }
    }

    get issue(): JobIssue | undefined {
        return this.issues && this.issues.last();
    }

    get vehicle(): number | undefined | null {
        const lastDispatch = this.dispatches && this.dispatches.last();
        return lastDispatch && lastDispatch.vehicle && lastDispatch.vehicle.id;
    }
    get controlledContents(): KeyValue[] {
        return this.attr?.filter(({ key }) => key === 'Controlled Contents') || [];
    }

    hasControlledContents(content: string): boolean {
        return !!this.findControlledContents(content).length;
    }

    findControlledContents(content: string): KeyValue[] {
        return this.controlledContents.filter(({ value }) => value === content) || [];
    }

    addControlledContents = (value: string): void => {
        if (!this.findControlledContents(value).length)
            this.attr?.push(new KeyValue({ key: 'Controlled Contents', value }));
    };

    removeControlledContents = (content: string): void =>
        this.findControlledContents(content).forEach((kvPair) => this.attr?.remove(kvPair));

    constructor(init?: Partial<Job>) {
        makeObservable(this, {
            name: observable,
            group: observable,
            status: observable,
            finished: observable,
            contact: observable,
            optedInForCampaignSms: observable,
            timeframe: observable,
            tracking: observable,
            tasks: observable,
            notes: observable,
            attr: observable,
            uploads: observable,
            chat: observable,
            progress: observable,
            dispatches: observable,
            policy: observable,
            ratings: observable,
            campaign: observable,
            cancelled: observable,
            offered: observable,
            site: observable,
            route: observable,
            issues: observable,
            external: observable,
            payable: observable,
            lines: observable,
            instructions: observable,
            courierInfo: observable,
            underway: computed,
            dispatched: computed,
            nextDeadline: computed,
            risk: computed,
            issue: computed,
            vehicle: computed,
            controlledContents: computed,
            addControlledContents: action,
            removeControlledContents: action
        });

        Object.assign(this, init);
    }
}

createModelSchema(Job, {
    id: optional(primitive()),
    created: optional(primitive()),
    name: optional(primitive()),
    group: optional(primitive()),
    status: optional(primitive()),
    finished: optional(primitive()),
    optedInForCampaignSms: optional(primitive()),
    contact: optional(object(Contact)),
    timeframe: optional(object(JobTimeframe)),
    tracking: optional(primitive()),
    tasks: indexList(Task),
    notes: optional(entityList(Note)),
    attr: optional(entityList(KeyValue)),
    uploads: optional(entityList(Upload)),
    chat: optional(entityList(Chat)),
    progress: optional(primitive()),
    dispatches: optional(entityList(Dispatch)),
    policy: optional(object(Policy)),
    ratings: optional(entityList(Rating)),
    campaign: optional(object(Campaign)),
    cancelled: optional(object(JobCancellation)),
    offered: optional(entityList(Vehicle)),
    site: optional(object(Site)),
    route: indexList(RouteLeg),
    issues: optional(entityList(JobIssue)),
    external: optional(primitive()),
    payable: optional(object(JobPayable)),
    // type: optional(primitive()),
    lines: optional(entityList(LineItem)),
    instructions: optional(primitive()),
    courierInfo: optional(entityList(CourierInfo))
});
